/**
 * CashFlowChart — Inflows / Outflows / Net Cash Flow per period.
 *
 * Props:
 *   transactions: Array<{ transactionDate: string, amount: number }>
 *   height:       number  (SVG viewBox height, default 220)
 *   compact:      boolean (smaller axis labels, default false)
 *
 * Matches the Trello reference for this ask: one bar per period straddling a
 * shared zero baseline (inflow up in green, outflow down in red), with a
 * connected "Net Cash Flow" line (inflow - outflow for that period) overlaid
 * on top -- not two same-direction bars side by side.
 *
 * All rendering is pure SVG — no third-party chart lib, matching NetWorthChart.
 * Sign convention mirrors report-sankey-analysis.js: amount > 0 is money coming
 * in (inflow), amount < 0 is money going out (outflow).
 */

import React, { useMemo } from 'react';
import { formatAxisDollar, niceAxisTicks } from './NetWorthChart';
import styles from './CashFlowChart.module.css';
import { isNonCashTransaction } from '../../utils/categoryOptions';

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[month - 1]} '${String(year).slice(2)}`;
}

// Bucket transactions into calendar months, summing inflow (amount > 0) and
// outflow (amount < 0, stored as a positive magnitude) separately per month.
// Non-cash rows (investment buys/sells/sweeps, valuations) are not cash flow
// and are skipped (categorization-segments ADR-001).
export function bucketCashFlowByMonth(transactions) {
  const buckets = new Map();
  for (const t of transactions || []) {
    if (isNonCashTransaction(t)) continue;
    const date = t?.transactionDate;
    const amount = t?.amount;
    if (!date || typeof amount !== 'number') continue;
    const monthKey = date.slice(0, 7); // 'YYYY-MM'
    if (!/^\d{4}-\d{2}$/.test(monthKey)) continue;
    if (!buckets.has(monthKey)) buckets.set(monthKey, { monthKey, inflow: 0, outflow: 0 });
    const bucket = buckets.get(monthKey);
    if (amount > 0) bucket.inflow += amount;
    else bucket.outflow += -amount;
  }
  return Array.from(buckets.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

export default function CashFlowChart({ transactions, height = 220, compact = false }) {
  const points = useMemo(() => bucketCashFlowByMonth(transactions), [transactions]);

  if (!points || points.length === 0) return null;

  const marginLeft = compact ? 55 : 65;
  const marginRight = 16;
  const marginTop = 12;
  const marginBottom = compact ? 28 : 32;
  const viewWidth = 800;
  const viewHeight = height;
  const plotW = viewWidth - marginLeft - marginRight;
  const plotH = viewHeight - marginTop - marginBottom;

  // Domain spans negative (outflow) to positive (inflow) around a shared zero
  // baseline -- always includes zero even if every period is all-inflow or
  // all-outflow, so the baseline is never at a plot edge.
  const rawMin = Math.min(0, ...points.map((p) => -p.outflow));
  const rawMax = Math.max(0, ...points.map((p) => p.inflow));
  const { ticks: yTicks, niceMin, niceMax } = niceAxisTicks(rawMin, rawMax, compact ? 4 : 5);
  const yRange = niceMax - niceMin || 1;
  const yFor = (value) => marginTop + plotH - ((value - niceMin) / yRange) * plotH;
  const zeroY = yFor(0);

  const groupW = plotW / points.length;
  const barW = Math.max(3, groupW * 0.5);

  // X-axis: pick evenly spaced month labels (max ~6-8), same approach as
  // NetWorthChart -- labeling every month overlaps badly once there are more
  // than a handful (a case spanning a few years easily has 40+ months).
  const maxXLabels = compact ? 5 : 8;
  const xLabelStep = Math.max(1, Math.ceil(points.length / maxXLabels));
  const xLabels = [];
  for (let i = 0; i < points.length; i += xLabelStep) {
    xLabels.push({ index: i, label: formatMonthLabel(points[i].monthKey) });
  }
  if (xLabels.length === 0 || xLabels[xLabels.length - 1].index !== points.length - 1) {
    xLabels.push({ index: points.length - 1, label: formatMonthLabel(points[points.length - 1].monthKey) });
  }

  const centerX = (i) => marginLeft + (i + 0.5) * groupW;
  const netPoints = points.map((p, i) => ({
    x: centerX(i),
    y: yFor(p.inflow - p.outflow),
    net: p.inflow - p.outflow,
    monthKey: p.monthKey,
  }));
  const netLinePath = netPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className={styles.container}>
      <div className={styles.legend}>
        <span className={styles.legendItem}><span className={`${styles.legendSwatch} ${styles.legendInflow}`} />Inflows</span>
        <span className={styles.legendItem}><span className={`${styles.legendSwatch} ${styles.legendOutflow}`} />Outflows</span>
        <span className={styles.legendItem}><span className={styles.legendLine} />Net Cash Flow</span>
      </div>
      <svg
        className={styles.chartSvg}
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Inflows, outflows, and net cash flow per month"
      >
        {/* Y-axis grid lines + labels */}
        {yTicks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={tick}>
              <line x1={marginLeft} y1={y} x2={viewWidth - marginRight} y2={y} className={styles.gridLine} strokeWidth="1" />
              <text x={marginLeft - 8} y={y} className={styles.yAxisLabel}>{formatAxisDollar(tick)}</text>
            </g>
          );
        })}

        {/* Zero baseline, drawn distinctly since bars diverge from it */}
        <line
          x1={marginLeft} y1={zeroY} x2={viewWidth - marginRight} y2={zeroY}
          stroke="var(--color-gray-400)" strokeWidth="1"
        />

        {/* Bars -- one per period, straddling the zero baseline */}
        {points.map((p, i) => {
          const x = centerX(i) - barW / 2;
          const inflowY = yFor(p.inflow);
          const outflowY = yFor(-p.outflow);
          return (
            <g key={p.monthKey}>
              <rect x={x} y={inflowY} width={barW} height={Math.max(0, zeroY - inflowY)} fill="var(--color-success-500)" rx="1">
                <title>{`${formatMonthLabel(p.monthKey)} inflow: ${formatAxisDollar(p.inflow)}`}</title>
              </rect>
              <rect x={x} y={zeroY} width={barW} height={Math.max(0, outflowY - zeroY)} fill="var(--color-error-500)" rx="1">
                <title>{`${formatMonthLabel(p.monthKey)} outflow: ${formatAxisDollar(p.outflow)}`}</title>
              </rect>
            </g>
          );
        })}

        {/* Net Cash Flow line + point markers */}
        <path d={netLinePath} fill="none" stroke="var(--color-navy-700)" strokeWidth="2" />
        {netPoints.map((p) => (
          <circle key={p.monthKey} cx={p.x} cy={p.y} r="3" fill="var(--color-navy-700)">
            <title>{`${formatMonthLabel(p.monthKey)} net cash flow: ${formatAxisDollar(p.net)}`}</title>
          </circle>
        ))}

        {/* X-axis labels — sparse (see xLabels above), not one per bar group */}
        {xLabels.map(({ index, label }) => (
          <text key={index} x={centerX(index)} y={marginTop + plotH + 14} className={styles.xAxisLabel}>
            {label}
          </text>
        ))}

        {/* Bottom axis line */}
        <line
          x1={marginLeft} y1={marginTop + plotH} x2={viewWidth - marginRight} y2={marginTop + plotH}
          stroke="var(--color-gray-200)" strokeWidth="1"
        />
      </svg>
    </div>
  );
}
